import api from './api';

/**
 * Mode hors-ligne de la caisse.
 * - Cache local du catalogue (produits, clients, remises) horodaté.
 * - File d'attente des ventes créées sans réseau (clé d'idempotence UUID :
 *   le serveur garantit qu'une resynchronisation ne crée jamais de doublon).
 * - Synchronisation automatique au retour du réseau.
 */

const CLE_CACHE_PREFIXE = 'nexacom_cache_';
const CLE_FILE_VENTES = 'nexacom_ventes_attente';

export interface CacheHorodate<T> {
  date: string; // ISO
  donnees: T;
}

export interface VenteEnAttente {
  cle: string; // UUID d'idempotence
  creeLe: string; // ISO — moment réel de la vente (hors-ligne)
  payload: any; // corps du POST /ventes
  numeroProvisoire: string; // affiché sur le ticket en attendant le vrai numéro
  derniereErreur?: string; // renseignée si le serveur a REFUSÉ la vente (non-réseau)
}

export interface ResultatSync {
  synchronisees: number;
  refusees: number;
  restantes: number;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Repli (vieux navigateurs) : aléa suffisant pour une clé d'idempotence.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Erreur réseau (pas de réponse du serveur) vs refus applicatif (4xx/5xx). */
export function estErreurReseau(e: any): boolean {
  return !!e && !e.response;
}

export const offlineService = {
  // --- Cache du catalogue ---

  sauverCache<T>(nom: string, donnees: T): void {
    try {
      const entree: CacheHorodate<T> = { date: new Date().toISOString(), donnees };
      localStorage.setItem(CLE_CACHE_PREFIXE + nom, JSON.stringify(entree));
    } catch {
      /* stockage plein : le cache est un confort, pas une exigence */
    }
  },

  lireCache<T>(nom: string): CacheHorodate<T> | null {
    try {
      const brut = localStorage.getItem(CLE_CACHE_PREFIXE + nom);
      return brut ? (JSON.parse(brut) as CacheHorodate<T>) : null;
    } catch {
      return null;
    }
  },

  // --- File d'attente des ventes hors-ligne ---

  listerVentesEnAttente(): VenteEnAttente[] {
    try {
      return JSON.parse(localStorage.getItem(CLE_FILE_VENTES) || '[]');
    } catch {
      return [];
    }
  },

  private_sauverFile(file: VenteEnAttente[]): void {
    localStorage.setItem(CLE_FILE_VENTES, JSON.stringify(file));
  },

  /** Enregistre une vente hors-ligne ; retourne l'entrée créée (pour le ticket). */
  mettreEnAttente(payload: any): VenteEnAttente {
    const file = this.listerVentesEnAttente();
    const maintenant = new Date();
    const entree: VenteEnAttente = {
      cle: uuid(),
      creeLe: maintenant.toISOString(),
      numeroProvisoire: `HL-${maintenant.getHours().toString().padStart(2, '0')}${maintenant
        .getMinutes()
        .toString()
        .padStart(2, '0')}-${(file.length + 1).toString().padStart(2, '0')}`,
      payload: {
        ...payload,
        // Trace du moment réel de la vente (le createdAt serveur sera celui de la sync).
        notes: [payload.notes, `Vente hors-ligne du ${maintenant.toLocaleString('fr-FR')}`]
          .filter(Boolean)
          .join(' — '),
      },
    };
    file.push(entree);
    this.private_sauverFile(file);
    return entree;
  },

  /**
   * Tente d'envoyer toutes les ventes en attente.
   * - Succès -> retirée de la file (le serveur dédoublonne via la clé).
   * - Refus applicatif (4xx/5xx) -> conservée avec derniereErreur (visible en UI).
   * - Erreur réseau -> on s'arrête (toujours hors-ligne), le reste attend.
   */
  async synchroniser(): Promise<ResultatSync> {
    let file = this.listerVentesEnAttente();
    let synchronisees = 0;
    let refusees = 0;

    for (const vente of [...file]) {
      try {
        await api.post('/ventes', { ...vente.payload, idempotenceKey: vente.cle });
        file = file.filter((v) => v.cle !== vente.cle);
        this.private_sauverFile(file);
        synchronisees++;
      } catch (e: any) {
        if (estErreurReseau(e)) break; // toujours hors-ligne : on réessaiera
        // Refus du serveur (validation, licence...) : on garde la vente avec le motif.
        vente.derniereErreur = e.response?.data?.message || `Erreur ${e.response?.status}`;
        file = file.map((v) => (v.cle === vente.cle ? vente : v));
        this.private_sauverFile(file);
        refusees++;
      }
    }

    return { synchronisees, refusees, restantes: this.listerVentesEnAttente().length };
  },

  /** Abandonne définitivement une vente en attente (après refus serveur assumé). */
  abandonnerVente(cle: string): void {
    this.private_sauverFile(this.listerVentesEnAttente().filter((v) => v.cle !== cle));
  },
};
